import axios from 'axios';
import {observable, observe, toJS} from 'mobx';
import {memoize} from 'lodash';
import * as auth from './auth.js';
import * as zendesk from './zendesk.js';
import {show_loading_screen} from './loading.js';
import {is_whitelabel} from './consts.js';
import {whitelabel_info} from './whitelabel.js';
import { open_vm } from "./useRDP.js";
import {initializePendo} from "./pendo";
import { app_settings } from './SettingsController';


const listen_login = ({log_in, log_out}) => observe(auth.logged_in, () =>

  auth.logged_in.get() ? log_in() : log_out()

);


export const email = observable.box('');
export const full_name = observable.box('');
export const is_managed = observable.box();
export const user_id = observable.box();
export const is_ad_user = observable.box();
export const ad_user_id = observable.box();
export const user_whitelabel_status = observable.box();
export const disabled_share_view = observable.box(false);
export const show_chat = observable.box(false);
export const is_subaccount_user = observable.box(false);


listen_login({

    log_in: show_loading_screen(async () => {

        const obj = await axios({url: '/api/users/'});
        
        full_name.set(obj.data.results[0].profile.full_name);
        email.set(obj.data.results[0].email);
        const managedBy = obj.data.results[0].profile?.managed_by ?? null;
        is_managed.set(managedBy !== null); // true for debug
        user_id.set(obj.data.results[0].id);
        is_ad_user.set(!!obj.data.results[0].ad_user);
        user_whitelabel_status.set(obj.data.results[0].profile.denver_stage === 4)
        show_chat.set(obj.data.results[0].profile.show_chat);
        is_subaccount_user.set(!!obj.data.results[0].profile.is_subaccount_user);

        let os, architecture, accessLevel, appVersion, appChannel, autoUpdate, stagingBucket;

        if (window.isElectron) {
          const platform = window.api.platform();
          os = platform === 'win32' ? 'windows' : platform === 'darwin' ? 'mac' : 'linux';

          if (os === 'windows') {
            const arch = window.api.arch();
            architecture = arch === 'ia32' ? '32bit' : arch === 'arm64' ? 'arm' : '64bit';
            const isAdmin = await window.api.isWindowsAdmin();
            accessLevel = isAdmin ? 'admin' : 'user';
          }

          [appVersion, appChannel, stagingBucket] = await Promise.all([
            window.api.appVersion(),
            window.api.appChannel(),
            window.api.getStagingBucket(),
          ]);

          autoUpdate = app_settings.get().autoUpdate;
        }

        initializePendo({
          userId: obj.data.results[0].id,
          managedBy,
          os,
          architecture,
          accessLevel,
          appVersion,
          appChannel,
          autoUpdate,
          stagingBucket,
        });

        if(obj.data.results[0].ad_user) {
          ad_user_id.set(obj.data.results[0].ad_user.id);
        }

        if(is_whitelabel) {

            const info = obj.data.results[0].whitelabel_info;

            // Strip trailing slash to be consistent with const.web_api_entrypoint
            // See auth.js:99
            if(info?.endpoint?.endsWith('/')) {
              info.endpoint = info.endpoint.slice(0, -1);
            }

          whitelabel_info.set(info);
        }

        if(is_whitelabel || user_whitelabel_status.get()){
            const titleElement = document.querySelector('title');
            const imageElement = document.querySelector('#thumbnail');
            titleElement.textContent = 'Cloud Workstations Webconnect';
            imageElement.setAttribute('href', 'images/icon-whitelabel.png');
        }

        if(show_chat.get()){
            zendesk.initializeZendesk(obj.data.results[0].zendesk_chat_token)
        }
        
        setTimeout(() => show_loading_screen(load_permissions_and_vms)(), 0);
    }),

    log_out: () => {

        full_name.set('');
        email.set('');
        zendesk.cleanupZendesk();
        is_managed.set();
        user_id.set();
        is_ad_user.set();
        ad_user_id.set();
        user_whitelabel_status.set();
        show_chat.set(false);
        is_subaccount_user.set(false);

        discard_data();

    }

});


export const user_list = observable.box({});

// window.user_list = () => toJS(user_list.get());

// {
//   178: { .. },
//   199: { .. }
// }

const set_user_list = obj => {

  // Refresh identity to force reassignment of the main user_list observable.

  user_list.set(Object.assign({}, obj));

};


export const vms_list = observable.box({});

// {
//    200: { ... }
//    203: { ... }
// }

export const vms_list_original = observable.box({});


export const load_vms = async () => {
    const vms = await axios({url: `/api/vms/?all=true`});
    vms_list_original.set(Array.isArray(vms.data) ? vms.data : vms.data.results);
}

export const load_permissions_and_vms = () =>
  (async () => {
    // Load VM inventory first so permission-mapped VMs can reuse canonical
    // descriptor/state fields when permission payloads are sparse.
    await load_vms();
    await load_permissions();
  })()

const find_original_vm = vm_id =>
  vms_list_original.get().find(({id}) => String(id) === String(vm_id));

const vm_belongs_to_pool = (vm, pool_id) => {
  const pools = vm && vm.pools;
  if(!Array.isArray(pools) || !pool_id) return false;

  return pools.some(pool => {
    const id = (pool && typeof pool === 'object') ? pool.id : pool;
    return String(id) === String(pool_id);
  });
};

const get_pool_readiness_state = (pool_id, fallback_vm) => {
  const all_vms = vms_list_original.get();
  const pool_vms = Array.isArray(all_vms)
    ? all_vms.filter(vm => vm_belongs_to_pool(vm, pool_id))
    : [];

  if(pool_vms.length > 0) {
    const has_running_vm = pool_vms.some(vm =>
      String(vm && vm.state).toLowerCase() === 'running'
    );
    return has_running_vm ? 'available' : 'pending';
  }

  return (fallback_vm && String(fallback_vm.state).toLowerCase() === 'running')
    ? 'available'
    : 'pending';
};

const normalize_vm = (vm, fallback = {}) => {
  if(!vm || !vm.id) return vm;

  const original_vm = find_original_vm(vm.id) || {};

  const descriptor = vm.descriptor
    || vm.description
    || vm.name
    || original_vm.descriptor
    || original_vm.description
    || original_vm.name
    || fallback.descriptor
    || '';

  const state = vm.state
    || vm.status
    || original_vm.state
    || fallback.state;

  return {
    ...original_vm,
    ...vm,
    ...(fallback.is_vmpool ? {is_vmpool: true} : {}),
    descriptor,
    ...(state ? {state} : {})
  };
};

export const load_permissions = async () => {

  if(is_ad_user.get()) return await load_ad_user_permissions();


  window.permission_list = () => toJS(permission_list.get());


  const vm_out = {};
  const perm_out = {};

  const permissions = await axios({url: `/api/permissions/?vmuser_user=${user_id.get()}&user=${user_id.get()}&all=true`});

  const disableShareView = permissions.data[0]?.disable_share_view;

  disabled_share_view.set(!!disableShareView);

  permissions.data.forEach(perm => {

    // Some high-level transformations to make the vmpool interface consistent with the vm interface

    const original_vm = normalize_vm(perm.vm);

    if(perm.app && perm.vmpool) {
      perm.type = 'vmpool-app';
    }

    if(perm.vmpool) {

      // Preserve the currently allocated VM (if present) so autoscaled
      // pool VMs can still appear in the desktop client computer list.
      perm.pool_vm = original_vm || null;

      perm.vm = {
        id: perm.vmpool.id,
        descriptor: perm.vmpool.name,
        is_vmpool: true,
        // Pool is available if at least one VM in it is running.
        state: get_pool_readiness_state(perm.vmpool.id, original_vm)
      };

      perm.vmpool = null;

    }

    // end

    perm.vm = normalize_vm(perm.vm, {
      descriptor: perm.pool_vm && perm.pool_vm.descriptor,
      is_vmpool: !!(perm.vm && perm.vm.is_vmpool)
    });
    if(perm.pool_vm) {
      perm.pool_vm = normalize_vm(perm.pool_vm, {
        descriptor: perm.vm && perm.vm.descriptor
      });
    }

    perm_out[perm.id] = perm;

    if(perm.vm && perm.vm.id) {
      vm_out[perm.vm.id] = perm.vm;
    }
    if(perm.pool_vm && perm.pool_vm.id) {
      vm_out[perm.pool_vm.id] = perm.pool_vm;
    }

  });

  permission_list.set(perm_out);
  vms_list.set(vm_out);

  if(is_managed.get() && Object.entries(permission_list.get()).length === 1) {
    open_vm(Object.keys(permission_list.get())[0]);
  }

};


const load_ad_user_permissions = async () => {

  const accesses = (await axios({url: `/api/adusers/${ad_user_id.get()}/accesses`})).data;

  const perm_out = {};
  const vm_out = {};


  accesses.forEach(access => {

    let id;
    const original_vm = normalize_vm(access.vm);

    // mimic the permission interface
    if(access.app) {
      id = access.app.id;

      access.type = access.vmpool ? 'vmpool-app' : "app";
    } else {
      id = access.id;

      access.type = access.vmpool ? 'vmpool' : "vm";
    }

    if(access.vmpool) {

      // Preserve the currently allocated VM (if present) so autoscaled
      // pool VMs can still appear in the desktop client computer list.
      access.pool_vm = original_vm || null;

      access.vm = {
        id: access.vmpool.id,
        descriptor: access.vmpool.name,
        is_vmpool: true,
        state: get_pool_readiness_state(access.vmpool.id, original_vm)
      };

      access.vmpool = null;

    }

    // mimic the same vm interface as with non-ad users
    // that way filtering logic in splash.js is consistent
    access.user = {id: user_id.get()};

    // end
    access.vm = normalize_vm(access.vm, {
      descriptor: access.pool_vm && access.pool_vm.descriptor,
      is_vmpool: !!(access.vm && access.vm.is_vmpool)
    });
    if(access.pool_vm) {
      access.pool_vm = normalize_vm(access.pool_vm, {
        descriptor: access.vm && access.vm.descriptor
      });
    }

    perm_out[id] = access;

    if(access.vm && access.vm.id) {
      vm_out[access.vm.id] = access.vm;
    }
    if(access.pool_vm && access.pool_vm.id) {
      vm_out[access.pool_vm.id] = access.pool_vm;
    }

  });

  permission_list.set(perm_out);
  vms_list.set(vm_out);

};


export const discard_data = () => {

  vms_list.set({});
  user_list.set({});
  permission_list.set({});

  // eslint-disable-next-line
  fetch_user_permissions.cache = new memoize.Cache;

};


export const permission_list = observable.box({});

// and then you have the actual, specific permissions with the rdp info & webconnect links that aren't cached ANYWHERE.
// or there's a flag that lets you flush the cache and force another request.

// {
//   100: { .. }
//   'aduser_120': { .. }
// }


const set_permission_list = obj => {

  // Refresh identity to force reassignment of the main permission_list observable.

  permission_list.set(Object.assign({}, obj));

};


export const fetch_permission = async perm_id => {

  try {

    const obj = await axios({
      url: `/api/permissions/${perm_id}?all=true`,
    });

    const _permission_list = permission_list.get();


    _permission_list[perm_id] = obj.data;

    set_permission_list(_permission_list);

    return _permission_list[perm_id];

  } catch(e) {

    console.error('bad permission lookup: ' + perm_id);

    return;

  }

};


// distinct from the above "fetch_permissions" as this applies also to vmusers that
// do not correspond to the currently logged-in user.

// this is used when clicking on a vmuser in the sidebar

export const fetch_user_permissions = memoize(async (vmuser_id, vm_id) => {

  const user_id = user_list.get()[vm_id].find(({id}) => id === vmuser_id).user.id;

  const vms = await axios({
    url: `/api/permissions/?vmuser=${vmuser_id}&user=${user_id}&all=true`
  });

  const permissions = vms.data.filter(({type}) => type !== 'vmpool');


  const _permission_list = permission_list.get();

  permissions.forEach(permission => {

    _permission_list[permission.id] = permission;

  });

  set_permission_list(_permission_list);


  return permissions;

});


export const fetch_vmusers = async vm_id => {

  const vmusers = (await axios({url: `/api/vmusers/?vm=${vm_id}&all=true`})).data;

  const _user_list = user_list.get();


  _user_list[vm_id] = [];

  vmusers.forEach(vmuser => {
    _user_list[vm_id].push(vmuser);
  });

  set_user_list(_user_list);

};
